import React, { useState } from 'react';
import { X, Check, ArrowRight, ArrowLeft, Pill, Package, DollarSign, ClipboardList, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { medicationsAPI } from '../../lib/api';
import { formatStock, formatCurrency } from '../../lib/utils';
import { MEDICATION_CATEGORIES, MEDICATION_PRESENTATIONS, MEDICATION_UNITS, ADMINISTRATION_TYPES } from './medicationConstants';

const STEPS = [
  { id: 1, title: 'Identificación', icon: ClipboardList, description: 'Nombre, categoría y tipo' },
  { id: 2, title: 'Presentación', icon: Package, description: 'Formato, unidad y proveedor' },
  { id: 3, title: 'Stock & Precio', icon: DollarSign, description: 'Cantidad y valor' },
];

const CATEGORY_COLORS = {
  'Antibiótico': 'bg-red-50 border-red-300 text-red-700',
  'Vitamina': 'bg-amber-50 border-amber-300 text-amber-700',
  'Vacuna': 'bg-blue-50 border-blue-300 text-blue-700',
  'Desparasitante': 'bg-emerald-50 border-emerald-300 text-emerald-700',
  'Antiinflamatorio': 'bg-purple-50 border-purple-300 text-purple-700',
  'Analgésico': 'bg-pink-50 border-pink-300 text-pink-700',
  'Suplemento': 'bg-teal-50 border-teal-300 text-teal-700',
  'Hormonal': 'bg-indigo-50 border-indigo-300 text-indigo-700',
  'Protector Hepático': 'bg-lime-50 border-lime-300 text-lime-700',
  'Otros': 'bg-slate-50 border-slate-300 text-slate-700',
};

const EMPTY = {
  name: '', category: '', administration_type: '', presentation: '', presentation_quantity: '',
  unit: '', supplier_id: '', min_stock: '', initial_quantity: '', purchase_price: '',
  currency: 'DOP', expiration_date: '',
};

export default function AddMedicationWizard({ open, onOpenChange, medications, suppliers, onSaved }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [existing, setExisting] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const suggestions = data.name.length >= 1 && !existing
    ? medications.filter((m) => m.name.toLowerCase().includes(data.name.toLowerCase()))
    : [];

  const duplicate = !existing && data.name.trim() && data.unit
    ? medications.find((m) => m.name.trim().toLowerCase() === data.name.trim().toLowerCase() && (m.unit || '').toLowerCase() === data.unit.toLowerCase())
    : null;

  const sameNameOtherUnit = !existing && data.name.trim()
    ? medications.filter((m) => m.name.trim().toLowerCase() === data.name.trim().toLowerCase())
    : [];

  const qty = parseFloat(data.initial_quantity) || 0;
  const price = parseFloat(data.purchase_price) || 0;
  const total = qty * price;
  const currencySymbol = data.currency === 'USD' ? 'US$' : 'RD$';

  const canNext = step === 1
    ? data.name.trim() && data.category
    : step === 2
      ? data.presentation && data.presentation_quantity && data.unit && !duplicate
      : true;

  const reset = () => { setStep(1); setData(EMPTY); setExisting(null); setShowSuggestions(false); };
  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  const selectExisting = (med) => {
    setExisting(med);
    setData({
      name: med.name,
      category: med.category || '',
      administration_type: med.administration_type || '',
      presentation: med.presentation || '',
      presentation_quantity: med.presentation_quantity || '',
      unit: med.unit || '',
      supplier_id: med.supplier_id || '',
      min_stock: med.min_stock || '',
      initial_quantity: '',
      purchase_price: med.purchase_price || '',
      currency: med.currency || 'DOP',
      expiration_date: '',
    });
    setShowSuggestions(false);
  };

  const clearExisting = () => { setExisting(null); setData(EMPTY); setStep(1); };

  const handleSave = async () => {
    if (qty <= 0) { toast.error('Ingresa la cantidad'); return; }
    if (price <= 0) { toast.error('Ingresa el precio unitario'); return; }
    if (duplicate) { toast.error(`Ya existe "${duplicate.name}" con unidad ${duplicate.unit}. Solo puede repetirse con una unidad diferente.`); return; }
    setSaving(true);
    try {
      if (existing) {
        const priceChanged = price !== existing.purchase_price;
        await medicationsAPI.createMovement({
          medication_id: existing.id,
          type: 'entrada',
          quantity: qty,
          unit_cost: price,
          currency: data.currency,
          notes: `Entrada${data.expiration_date ? `, Vence: ${data.expiration_date}` : ''}${priceChanged ? ` | Precio: ${price}` : ''}`
        });
        if (priceChanged) {
          const currentStock = existing.current_stock || 0;
          const totalStock = currentStock + qty;
          const weighted = totalStock > 0 ? ((currentStock * (existing.purchase_price || 0)) + (qty * price)) / totalStock : price;
          await medicationsAPI.update(existing.id, {
            purchase_price: parseFloat(weighted.toFixed(2)),
            expiration_date: data.expiration_date || existing.expiration_date
          });
          toast.success(`Stock agregado: +${qty}. Precio promedio ponderado: ${weighted.toFixed(2)}`);
        } else {
          if (data.expiration_date) {
            await medicationsAPI.update(existing.id, { expiration_date: data.expiration_date });
          }
          toast.success(`Stock agregado: +${qty} ${data.presentation}`);
        }
      } else {
        await medicationsAPI.create({
          name: data.name.trim(),
          presentation: data.presentation,
          presentation_quantity: parseFloat(data.presentation_quantity) || 0,
          category: data.category,
          administration_type: data.administration_type || null,
          initial_quantity: qty,
          min_stock: parseFloat(data.min_stock) || 0,
          unit: data.unit,
          currency: data.currency,
          supplier_id: data.supplier_id || null,
          purchase_price: price,
          expiration_date: data.expiration_date || null,
        });
        toast.success('Medicamento creado');
      }
      reset();
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden max-h-[95vh] overflow-y-auto" data-testid="medication-wizard-dialog">
        <div className="grid grid-cols-1 md:grid-cols-5">
          <div className="col-span-3 p-6 bg-white">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wider text-[#931A25] font-semibold">
                {existing ? 'Agregar Stock' : 'Nuevo Medicamento'}
              </span>
              <button onClick={() => handleClose(false)} className="text-slate-400 hover:text-slate-700" data-testid="wizard-close-btn">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-4">{existing ? existing.name : 'Nuevo Medicamento'}</h2>

            {existing && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-start justify-between gap-2" data-testid="wizard-existing-banner">
                <span>Agregando stock a un medicamento existente. Stock actual: <strong>{formatStock(existing.current_stock)} {existing.presentation}</strong></span>
                <button onClick={clearExisting} className="text-blue-600 underline text-xs whitespace-nowrap">Crear nuevo</button>
              </div>
            )}

            {/* Stepper */}
            <div className="flex items-center gap-2 mb-6">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = step === s.id;
                const done = step > s.id;
                return (
                  <React.Fragment key={s.id}>
                    <div className={`flex items-center gap-2 ${active ? 'text-[#931A25]' : done ? 'text-emerald-600' : 'text-slate-300'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                        active ? 'border-[#931A25] bg-[#931A25]/10 shadow-md' :
                        done ? 'border-emerald-500 bg-emerald-500 text-white' :
                        'border-slate-200 bg-white'
                      }`}>
                        {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                      </div>
                      <div className="hidden sm:block">
                        <p className="text-xs font-semibold leading-none">{s.title}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{s.description}</p>
                      </div>
                    </div>
                    {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${step > s.id ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="min-h-[300px]">
              {step === 1 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="space-y-1.5 relative">
                    <Label className="text-sm">Nombre del medicamento *</Label>
                    <Input
                      value={data.name}
                      onChange={(e) => { set('name', e.target.value); setShowSuggestions(true); }}
                      onFocus={() => !existing && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Ej: CENTREVIT-AD3E"
                      className="text-base h-11"
                      disabled={!!existing}
                      data-testid="wizard-name-input"
                    />
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border-2 border-amber-300 rounded-lg shadow-xl max-h-52 overflow-y-auto" data-testid="wizard-name-suggestions">
                        <div className="p-2 text-xs font-medium text-amber-700 border-b bg-amber-50 sticky top-0">
                          Ya existe en inventario — selecciónalo para agregar stock:
                        </div>
                        {suggestions.map((med) => (
                          <div key={med.id} className="px-3 py-2 hover:bg-amber-50 cursor-pointer border-b last:border-0"
                            onMouseDown={() => selectExisting(med)}>
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm text-slate-800">{med.name}</p>
                              <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">Stock: {formatStock(med.current_stock)}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {med.category} • {med.presentation || 'Sin presentación'} {med.presentation_quantity ? `(${med.presentation_quantity} ${med.unit})` : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    {sameNameOtherUnit.length > 0 && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-xs text-amber-800" data-testid="wizard-duplicate-warning">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                          Este nombre ya existe con unidad: <strong>{sameNameOtherUnit.map(m => m.unit).join(', ')}</strong>.
                          Solo podrás crearlo con una <strong>unidad diferente</strong>.
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Categoría *</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {MEDICATION_CATEGORIES.map((cat) => (
                        <button key={cat} type="button" disabled={!!existing}
                          onClick={() => set('category', cat)}
                          data-testid={`wizard-category-${cat}`}
                          className={`px-2 py-2 rounded-lg border-2 text-xs font-medium transition-all text-left disabled:opacity-60 ${
                            data.category === cat ? (CATEGORY_COLORS[cat] || 'bg-slate-100 border-slate-400 text-slate-700') + ' ring-2 ring-offset-1 ring-current' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Tipo de administración</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {ADMINISTRATION_TYPES.map((t) => (
                        <button key={t.value} type="button" disabled={!!existing}
                          onClick={() => set('administration_type', data.administration_type === t.value ? '' : t.value)}
                          data-testid={`wizard-admin-${t.value}`}
                          className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all disabled:opacity-60 ${
                            data.administration_type === t.value ? 'bg-[#931A25] border-[#931A25] text-white shadow-md' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                          }`}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="space-y-2">
                    <Label className="text-sm">¿Cómo viene presentado? *</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {MEDICATION_PRESENTATIONS.map((p) => (
                        <button key={p.value} type="button" disabled={!!existing}
                          onClick={() => set('presentation', p.value)}
                          data-testid={`wizard-presentation-${p.value}`}
                          className={`px-2 py-2.5 rounded-lg border-2 text-xs font-medium transition-all disabled:opacity-60 ${
                            data.presentation === p.value ? 'bg-[#931A25] border-[#931A25] text-white shadow-md' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                          }`}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm">Contenido por {MEDICATION_PRESENTATIONS.find(p => p.value === data.presentation)?.label?.toLowerCase() || 'unidad'} *</Label>
                      <Input type="number" step="0.01" min="0" value={data.presentation_quantity}
                        onChange={(e) => set('presentation_quantity', e.target.value)}
                        placeholder="Ej: 50" className="h-11 text-base" disabled={!!existing}
                        data-testid="wizard-content-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Unidad de medida *</Label>
                      <Select value={data.unit} onValueChange={(v) => set('unit', v)} disabled={!!existing}>
                        <SelectTrigger className="h-11" data-testid="wizard-unit-select">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {MEDICATION_UNITS.map((u) => (
                            <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {duplicate && (
                    <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg flex items-start gap-2 text-sm text-red-800" data-testid="wizard-duplicate-error">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>Ya existe <strong>{duplicate.name}</strong> con la unidad <strong>{duplicate.unit}</strong>. Cambia la unidad o regresa y selecciónalo para agregar stock.</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm">Proveedor</Label>
                      <Select value={data.supplier_id} onValueChange={(v) => set('supplier_id', v)}>
                        <SelectTrigger className="h-11" data-testid="wizard-supplier-select">
                          <SelectValue placeholder="Seleccionar proveedor..." />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Stock mínimo</Label>
                      <Input type="number" step="0.01" min="0" value={data.min_stock}
                        onChange={(e) => set('min_stock', e.target.value)}
                        placeholder="Ej: 5" className="h-11 text-base" disabled={!!existing}
                        data-testid="wizard-minstock-input" />
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm">{existing ? 'Cantidad a agregar *' : 'Cantidad a ingresar *'}</Label>
                      <div className="relative">
                        <Input type="number" step="0.01" min="0" value={data.initial_quantity}
                          onChange={(e) => set('initial_quantity', e.target.value)}
                          placeholder="0" className="h-14 text-2xl font-bold text-center"
                          data-testid="wizard-quantity-input" />
                        {data.presentation && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 capitalize">{data.presentation}s</span>}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Precio Unitario *</Label>
                      <div className="relative">
                        <Input type="number" step="0.01" min="0" value={data.purchase_price}
                          onChange={(e) => set('purchase_price', e.target.value)}
                          placeholder="0.00" className="h-14 text-2xl font-bold text-center pr-14"
                          data-testid="wizard-price-input" />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                          <button type="button" onClick={() => set('currency', 'DOP')}
                            className={`px-2 py-1 rounded text-xs font-bold ${data.currency === 'DOP' ? 'bg-[#931A25] text-white' : 'bg-slate-100 text-slate-500'}`}>RD$</button>
                          <button type="button" onClick={() => set('currency', 'USD')}
                            className={`px-2 py-1 rounded text-xs font-bold ${data.currency === 'USD' ? 'bg-[#931A25] text-white' : 'bg-slate-100 text-slate-500'}`}>US$</button>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">Precio POR UNIDAD (no el total).</p>
                    </div>
                  </div>
                  {total > 0 && (
                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl" data-testid="wizard-total-preview">
                      <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Total factura</p>
                      <p className="text-3xl font-bold text-emerald-900">{currencySymbol} {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="text-xs text-emerald-600 mt-1">{qty} × {currencySymbol} {price.toFixed(2)}</p>
                    </div>
                  )}
                  {existing && price !== existing.purchase_price && qty > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded p-2 text-xs text-green-700" data-testid="wizard-weighted-preview">
                      <p className="font-medium">Se calculará promedio ponderado:</p>
                      <p>
                        ({formatStock(existing.current_stock)} × {existing.purchase_price} + {formatStock(qty)} × {price}) / {formatStock(existing.current_stock + qty)} =
                        <strong className="ml-1">{(((existing.current_stock * existing.purchase_price) + (qty * price)) / (existing.current_stock + qty)).toFixed(2)}</strong>
                      </p>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Fecha de vencimiento (opcional)</Label>
                    <Input type="date" value={data.expiration_date} onChange={(e) => set('expiration_date', e.target.value)} className="h-11" data-testid="wizard-expiration-input" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="wizard-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Atrás
              </Button>
              <p className="text-xs text-slate-400">Paso {step} de {STEPS.length}</p>
              {step < STEPS.length ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canNext} className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="wizard-next-btn">
                  Siguiente <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSave} disabled={saving || qty <= 0 || price <= 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wizard-save-btn">
                  <Check className="h-4 w-4 mr-1" /> {saving ? 'Guardando...' : existing ? 'Agregar Stock' : 'Guardar'}
                </Button>
              )}
            </div>
          </div>

          {/* Live preview */}
          <div className="col-span-2 bg-gradient-to-b from-slate-900 to-slate-800 p-6 text-white hidden md:block">
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-3">Vista Previa</p>
            <div className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10">
              <div className="w-12 h-12 rounded-lg bg-[#931A25]/20 flex items-center justify-center mb-3">
                <Pill className="h-6 w-6 text-[#DC7178]" />
              </div>
              <h3 className="font-bold text-lg leading-tight" data-testid="wizard-preview-name">{data.name || <span className="text-slate-500 italic font-normal">Sin nombre</span>}</h3>
              <p className="text-xs text-slate-300 mt-1">
                {data.category || <span className="italic">Sin categoría</span>}
                {data.administration_type && <span className="ml-2 px-1.5 py-0.5 bg-white/10 rounded text-[10px] uppercase">{ADMINISTRATION_TYPES.find(t => t.value === data.administration_type)?.label}</span>}
              </p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between border-t border-white/10 pt-2">
                  <span className="text-slate-400">Presentación</span>
                  <span className="font-medium capitalize">{data.presentation ? `${MEDICATION_PRESENTATIONS.find(p => p.value === data.presentation)?.label || data.presentation}${data.presentation_quantity ? ` (${data.presentation_quantity} ${data.unit})` : ''}` : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Proveedor</span>
                  <span className="font-medium truncate max-w-[140px]">{suppliers.find(s => s.id === data.supplier_id)?.name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Stock mínimo</span>
                  <span className="font-medium">{data.min_stock || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Cantidad</span>
                  <span className="font-medium">{data.initial_quantity || '—'} {data.presentation && data.initial_quantity ? `${data.presentation}s` : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Precio unitario</span>
                  <span className="font-medium">{price > 0 ? `${currencySymbol} ${price.toFixed(2)}` : '—'}</span>
                </div>
                {total > 0 && (
                  <div className="flex justify-between border-t border-white/20 pt-2 mt-3">
                    <span className="text-slate-300 font-semibold">Total factura</span>
                    <span className="font-bold text-emerald-400 text-lg">{currencySymbol} {total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-4 leading-relaxed">La vista previa se actualiza en tiempo real conforme completas los pasos.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
